/**
 * Page de détail d'une intervention/demande client
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Mail,
  User,
  Phone,
  Building2,
  Calendar,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Edit3,
  Send,
  Clock,
  AlertCircle,
  FileText,
  ListTodo,
  Link2,
  Briefcase,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { Modal } from '@/components/ui/Modal'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { qontoApiCall, getQontoStatus } from '@/services/qontoApi'
import { useAuth } from '@/contexts/AuthContext'
import {
  useUpdateIntervention,
  useResolveIntervention,
  useAIConversation,
} from '@/hooks/useAI'
import {
  INTERVENTION_REASON_LABELS,
  type InterventionRequest,
} from '@/types/ai'
import { cn } from '@/lib/utils'

// Priority config
const PRIORITY_CONFIG: Record<
  'low' | 'medium' | 'high' | 'urgent',
  { label: string; variant: 'red' | 'gold' | 'blue' | 'gray'; icon: typeof AlertCircle }
> = {
  urgent: { label: 'Urgent', variant: 'red', icon: AlertCircle },
  high: { label: 'Haute', variant: 'gold', icon: AlertCircle },
  medium: { label: 'Moyenne', variant: 'blue', icon: Clock },
  low: { label: 'Basse', variant: 'gray', icon: Clock },
}

// Status config
const STATUS_CONFIG: Record<
  string,
  { label: string; variant: 'gold' | 'blue' | 'mint' | 'gray' | 'red' }
> = {
  pending: { label: 'En attente', variant: 'gold' },
  in_progress: { label: 'En cours', variant: 'blue' },
  resolved: { label: 'Resolue', variant: 'mint' },
  dismissed: { label: 'Ignoree', variant: 'gray' },
  draft: { label: 'Brouillon', variant: 'gray' },
  submitted: { label: 'Soumise', variant: 'gold' },
  completed: { label: 'Terminée', variant: 'mint' },
}

export default function InterventionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showResponseModal, setShowResponseModal] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [responseText, setResponseText] = useState('')
  const [adminNotes, setAdminNotes] = useState('')
  const [isAddingToQuote, setIsAddingToQuote] = useState(false)
  const [isCreatingTask, setIsCreatingTask] = useState(false)
  const [isCreatingProject, setIsCreatingProject] = useState(false)
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false)

  // Fetch intervention details
  const { data: intervention, isLoading } = useQuery({
    queryKey: ['intervention', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_requests')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      // Map to InterventionRequest format
      return {
        id: data.id,
        conversationId: data.conversation_id || '',
        createdAt: data.created_at,
        reason: data.request_type,
        context: data.description || '',
        lastUserMessage: data.title,
        priority: data.priority || 'medium',
        assignedAdminId: data.admin_assigned_id,
        status: data.status,
        resolvedAt: data.completed_at,
        adminNotes: data.assigned_admin_notes,
        userId: data.user_id,
      } as InterventionRequest & { userId: string }
    },
    enabled: !!id,
  })

  // Fetch user details (from users table, not clients)
  const { data: clientUser } = useQuery({
    queryKey: ['user', intervention?.userId],
    queryFn: async () => {
      if (!intervention?.userId) return null
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', intervention.userId)
        .single()

      if (error) return null
      return data
    },
    enabled: !!intervention?.userId,
  })

  // Fetch full intervention data with jsonb data field
  const { data: fullInterventionData } = useQuery({
    queryKey: ['intervention-full', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_requests')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  // Fetch AI conversation if exists
  const { data: conversation } = useAIConversation(intervention?.conversationId)

  // Fetch project if this is a new_idea type
  const projectId = fullInterventionData?.data?.project_id
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (!projectId) return null
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single()

      if (error) return null
      return data
    },
    enabled: !!projectId,
  })

  // Check if this is a new idea
  const isNewIdea = fullInterventionData?.data?.type === 'new_idea' ||
                    fullInterventionData?.data?.type === 'project_idea'

  const updateIntervention = useUpdateIntervention()
  const resolveIntervention = useResolveIntervention()

  const handleTakeOver = () => {
    if (!user?.id || !intervention) return
    updateIntervention.mutate({
      id: intervention.id,
      updates: { status: 'in_progress', assignedAdminId: user.id },
    })
  }

  const handleUpdateStatus = (status: string) => {
    if (!intervention) return
    updateIntervention.mutate({
      id: intervention.id,
      updates: { status: status as any },
    })
  }

  const handleSendEmail = async () => {
    if (!clientUser?.email || !emailSubject || !emailBody) return

    // TODO: Implement email sending via your email service
    console.log('Sending email:', { to: clientUser.email, subject: emailSubject, body: emailBody })

    setShowEmailModal(false)
    setEmailSubject('')
    setEmailBody('')
  }

  const handleSubmitResponse = () => {
    if (!intervention || !responseText.trim() || !user?.id) return

    resolveIntervention.mutate(
      {
        interventionId: intervention.id,
        adminId: user.id,
        response: responseText,
        notes: adminNotes || undefined,
      },
      {
        onSuccess: () => {
          setShowResponseModal(false)
          setResponseText('')
          setAdminNotes('')
          queryClient.invalidateQueries({ queryKey: ['intervention', id] })
        },
      }
    )
  }

  const handleAddToQuote = async () => {
    if (!fullInterventionData || !projectId) return
    setIsAddingToQuote(true)

    try {
      // Navigate to create a new quote with pre-filled project info
      navigate(`/devis/new?project_id=${projectId}&idea_title=${encodeURIComponent(intervention?.lastUserMessage || '')}&idea_description=${encodeURIComponent(intervention?.context || '')}`)
    } catch (error) {
      console.error('Error navigating to quote:', error)
    } finally {
      setIsAddingToQuote(false)
    }
  }

  const handleCreateTask = async () => {
    if (!fullInterventionData || !projectId) return
    setIsCreatingTask(true)

    try {
      // Get max order_index for this project
      const { data: maxOrderData } = await supabase
        .from('project_phases')
        .select('order_index')
        .eq('project_id', projectId)
        .order('order_index', { ascending: false })
        .limit(1)
        .single()

      const order_index = (maxOrderData?.order_index || 0) + 1

      const { error } = await supabase
        .from('project_phases')
        .insert({
          project_id: projectId,
          name: intervention?.lastUserMessage || 'Nouvelle idée',
          description: intervention?.context || fullInterventionData.description,
          status: 'pending',
          order_index,
          notes: `Créée depuis l'intervention: ${intervention?.lastUserMessage || 'Sans titre'} (ID: ${intervention?.id?.slice(0, 8) || 'N/A'})`,
        })

      if (error) {
        console.error('Error creating task:', error)
        alert('Erreur lors de la création de la tâche')
      } else {
        alert('Tâche créée avec succès !')
        queryClient.invalidateQueries({ queryKey: ['project', projectId, 'tasks'] })
        queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      }
    } catch (error) {
      console.error('Error creating task:', error)
      alert('Erreur lors de la création de la tâche')
    } finally {
      setIsCreatingTask(false)
    }
  }

  const handleCreateProject = async () => {
    if (!fullInterventionData || !intervention?.userId) return
    setIsCreatingProject(true)

    try {
      // Extract project data from intervention
      const projectData = fullInterventionData.data || {}
      const projectType = projectData.project_type || 'autre'
      const answers = projectData.answers || {}
      const bmadMarkdown = projectData.bmad_markdown || fullInterventionData.description

      // --- Helper: format array or string ---
      const formatValue = (value: unknown): string => {
        if (Array.isArray(value)) return value.join(', ')
        if (typeof value === 'string') return value
        return ''
      }

      // --- Extract structured client-facing data from answers ---
      const goalsParts = [
        answers.q4_objectives && formatValue(answers.q4_objectives),
        answers.q_kpi_1,
      ].filter(Boolean)
      const goals = goalsParts.join('\n') || null

      const features = answers.q_features_mvp || null
      const target = answers.q_persona_1 || null

      // --- Generate client-friendly description ---
      const projectTypeLabels: Record<string, string> = {
        refonte: 'Refonte de site web',
        ecommerce: 'Site e-commerce',
        vitrine: 'Site vitrine',
        application: 'Application web',
        blog: 'Blog / site de contenu',
        landing: 'Landing page',
        autre: 'Projet web',
      }
      const typeLabel = projectTypeLabels[projectType] || projectTypeLabels['autre']

      const descParts = [
        `${typeLabel}${answers.q1_url ? ` pour ${answers.q1_url}` : ''}`,
        answers.q_design_style ? `Style souhaité : ${answers.q_design_style}` : '',
        answers.q4_objectives ? `Objectifs : ${formatValue(answers.q4_objectives)}` : '',
        answers.q_page_volume ? `Volume : ${answers.q_page_volume}` : '',
      ].filter(Boolean)
      const clientDescription = descParts.join('. ') + '.'

      // --- Random thumbnail from available images ---
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const thumbnailImages = ['c1.png', 'c2.png', 'info1.png', 'info2.jpg']
      const randomThumb = thumbnailImages[Math.floor(Math.random() * thumbnailImages.length)]
      const thumbnailUrl = `${supabaseUrl}/storage/v1/object/public/img/${randomThumb}`

      // Create the project with all client-facing fields populated
      const { data: newProject, error: projectError } = await supabase
        .from('projects')
        .insert({
          title: intervention.lastUserMessage || 'Nouveau projet',
          description: clientDescription,
          user_id: intervention.userId,
          company: clientUser?.company || null,
          contact_name: clientUser?.full_name || null,
          contact_email: clientUser?.email || null,
          site_type: projectType,
          goals,
          features,
          target,
          budget: answers.budget || null,
          deadline: null,
          thumbnail_url: thumbnailUrl,
          support_contact: 'Thymoa Gaigeard',
          support_email: 'support@otisite.com',
          support_needed: true,
          status: 'pending',
          progress: 0,
          metadata: {
            bmad_markdown: bmadMarkdown,
            bmad_json: projectData.bmad_json || null,
            raw_answers: answers,
            intervention_id: intervention.id,
            project_type: projectType,
          },
        } as any)
        .select()
        .single()

      if (projectError) {
        console.error('Error creating project:', projectError)
        alert('Erreur lors de la création du projet: ' + projectError.message)
        return
      }

      // Update intervention to mark as converted to project
      const updatedData = {
        ...(fullInterventionData.data || {}),
        converted_to_project_id: newProject.id,
        converted_at: new Date().toISOString(),
        project_type: projectType,
      }

      await supabase
        .from('client_requests')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          assigned_admin_notes: `Converti en projet: ${newProject.id}`,
          data: updatedData,
        })
        .eq('id', intervention.id)

      // Sync avec Qonto : créer un devis pour le nouveau projet (non bloquant)
      try {
        const qontoStatus = await getQontoStatus()
        if (qontoStatus.connected && intervention.userId) {
          // Trouver ou créer le client Qonto
          const { data: userData } = await supabase
            .from('users')
            .select('qonto_client_id, full_name, email, company')
            .eq('id', intervention.userId)
            .single()

          let qontoClientId = userData?.qonto_client_id || null

          if (!qontoClientId && userData) {
            const isCompany = !!userData.company
            const clientBody = isCompany
              ? { name: userData.company, email: userData.email, type: 'company' }
              : { first_name: (userData.full_name || 'Client').split(' ')[0], last_name: (userData.full_name || '').split(' ').slice(1).join(' ') || '-', email: userData.email, type: 'individual' }

            const clientResult = await qontoApiCall<{ client: { id: string } }>('/clients', {
              method: 'POST',
              body: clientBody,
            })
            if (clientResult?.client?.id) {
              qontoClientId = clientResult.client.id
              await supabase.from('users').update({ qonto_client_id: clientResult.client.id }).eq('id', intervention.userId)
            }
          }

          if (qontoClientId) {
            // Assurer que le client a locale/currency/billing_address
            await qontoApiCall(`/clients/${qontoClientId}`, {
              method: 'PATCH',
              body: { locale: 'fr', currency: 'EUR', billing_address: { street_address: '-', city: '-', zip_code: '00000', country_code: 'FR' } },
            }).catch(() => {})

            const budget = answers.budget ? parseFloat(String(answers.budget).replace(/[^\d.]/g, '')) : 0
            const issueDate = new Date().toISOString().split('T')[0]
            const expiryDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

            await qontoApiCall('/quotes', {
              method: 'POST',
              body: {
                client_id: qontoClientId,
                issue_date: issueDate,
                expiry_date: expiryDate,
                currency: 'EUR',
                terms_and_conditions: '',
                items: [{
                  title: newProject.title || 'Projet web',
                  quantity: '1',
                  unit_price: { value: String(budget || 0), currency: 'EUR' },
                  vat_rate: '0.20',
                  currency: 'EUR',
                }],
              },
            })
          }
        }
      } catch (qontoError) {
        console.error('Erreur sync Qonto projet:', qontoError)
      }

      alert('✅ Projet créé avec succès !')
      setShowCreateProjectModal(false)

      // Redirect to the new project
      navigate(`/projects/${newProject.id}`)
    } catch (error) {
      console.error('Error creating project:', error)
      alert('Erreur lors de la création du projet')
    } finally {
      setIsCreatingProject(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!intervention) {
    return (
      <div className="text-center py-12">
        <p className="text-brown-600">Intervention non trouvée</p>
      </div>
    )
  }

  const priorityConfig = PRIORITY_CONFIG[intervention.priority as keyof typeof PRIORITY_CONFIG] || PRIORITY_CONFIG.medium
  const statusConfig = STATUS_CONFIG[intervention.status] || { label: intervention.status, variant: 'gray' as const }
  const PriorityIcon = priorityConfig.icon

  const breadcrumbItems = [
    { label: 'Interventions', href: '/interventions' },
    { label: `Intervention #${intervention.id.slice(0, 8)}` },
  ]

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/interventions')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-display font-bold text-brown-900">
              {intervention.lastUserMessage}
            </h1>
            <p className="text-sm text-brown-600 mt-1">
              Créée le {new Date(intervention.createdAt).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant={priorityConfig.variant} size="md">
            {priorityConfig.label}
          </Badge>
          <Badge variant={statusConfig.variant} size="md">
            {statusConfig.label}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Intervention details */}
          <Card variant="bordered">
            <div className="flex items-start gap-3 mb-4">
              <div
                className={cn(
                  'w-12 h-12 rounded-full flex items-center justify-center',
                  `bg-${priorityConfig.variant}-100`
                )}
              >
                <PriorityIcon className={cn('w-6 h-6', `text-${priorityConfig.variant}-600`)} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-brown-900 mb-1">
                  Détails de la demande
                </h3>
                <Badge variant="gray" size="sm">
                  {INTERVENTION_REASON_LABELS[intervention.reason as keyof typeof INTERVENTION_REASON_LABELS] || intervention.reason}
                </Badge>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-brown-700 mb-2">Description</h4>
                <div className="bg-beige-50 rounded-lg p-4 text-sm text-brown-700 whitespace-pre-wrap">
                  {intervention.context || 'Aucune description disponible'}
                </div>
              </div>

              {intervention.adminNotes && (
                <div>
                  <h4 className="text-sm font-medium text-brown-700 mb-2">Notes de l'admin</h4>
                  <p className="text-sm text-brown-600">{intervention.adminNotes}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Question Response Special Section */}
          {intervention.request_type === 'question_response' && fullInterventionData?.data && (
            <Card variant="bordered" className="border-mint-300 bg-mint-50/30">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-mint-100">
                  <MessageSquare className="w-6 h-6 text-mint-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-brown-900 mb-1">
                    ✅ Réponses au questionnaire
                  </h3>
                  <p className="text-sm text-brown-600">
                    {fullInterventionData.data.question_title || 'Questionnaire client'}
                  </p>
                </div>
              </div>

              {/* Project Link */}
              {fullInterventionData.data.project_id && fullInterventionData.data.project_title && (
                <div className="bg-white rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-brown-500 mb-1">Projet concerné</p>
                      <p className="font-medium text-brown-900">{fullInterventionData.data.project_title}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/projects/${fullInterventionData.data.project_id}?tab=questions`)}
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Voir le projet
                    </Button>
                  </div>
                </div>
              )}

              {/* Questions and Answers */}
              <div className="bg-white rounded-lg p-4">
                <h4 className="text-sm font-medium text-brown-700 mb-4">Questions et réponses</h4>
                <div className="space-y-4">
                  {fullInterventionData.data.questions?.map((question: any) => {
                    const answer = fullInterventionData.data.responses?.[question.id];
                    if (!answer) return null;

                    return (
                      <div key={question.id} className="border-b border-beige-200 pb-4 last:border-0 last:pb-0">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-mint-100 flex items-center justify-center text-xs font-medium text-mint-700">
                            {question.id}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-brown-900 mb-2">
                              {question.question}
                              {question.required && <span className="text-red-500 ml-1">*</span>}
                            </p>
                            <div className="bg-beige-50 rounded-lg p-3">
                              <p className="text-sm text-brown-700 whitespace-pre-wrap">
                                {answer}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="gray" size="sm">
                                {question.type === 'text' && '📝 Texte court'}
                                {question.type === 'textarea' && '📄 Texte long'}
                                {question.type === 'multiple' && '☑️ Choix multiple'}
                                {question.type === 'number' && '🔢 Nombre'}
                                {question.type === 'date' && '📅 Date'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Submitted date */}
                {fullInterventionData.data.submitted_at && (
                  <div className="mt-4 pt-4 border-t border-beige-200">
                    <div className="flex items-center gap-2 text-xs text-brown-500">
                      <Calendar className="w-3 h-3" />
                      <span>
                        Répondu le {new Date(fullInterventionData.data.submitted_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* New Idea Special Section */}
          {isNewIdea && (
            <Card variant="bordered" className="border-blue-300 bg-blue-50/30">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-100">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-brown-900 mb-1">
                    💡 Nouvelle idée de projet
                  </h3>
                  <p className="text-sm text-brown-600">
                    Cette demande est une nouvelle idée pour enrichir un projet existant
                  </p>
                </div>
              </div>

              {/* Project Link */}
              {project && (
                <div className="bg-white rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-brown-500 mb-1">Projet lié</p>
                      <p className="font-medium text-brown-900">{project.name}</p>
                      {project.description && (
                        <p className="text-sm text-brown-600 mt-1">{project.description}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/projects/${projectId}`)}
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Voir le projet
                    </Button>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="bg-white rounded-lg p-4">
                <h4 className="text-sm font-medium text-brown-700 mb-3">Actions rapides</h4>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddToQuote}
                    disabled={isAddingToQuote || !projectId}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Ajouter au devis
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateTask}
                    disabled={isCreatingTask || !projectId}
                  >
                    {isCreatingTask ? (
                      <Spinner size="sm" className="mr-2" />
                    ) : (
                      <ListTodo className="w-4 h-4 mr-2" />
                    )}
                    Créer une tâche
                  </Button>
                  {projectId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/projects/${projectId}`)}
                    >
                      <Briefcase className="w-4 h-4 mr-2" />
                      Aller au projet
                    </Button>
                  )}
                </div>
                <p className="text-xs text-brown-500 mt-3">
                  💡 Conseil : Ajoutez cette idée au devis ou créez une tâche pour ne pas l'oublier
                </p>
              </div>
            </Card>
          )}

          {/* Project Data (from data jsonb field) */}
          {fullInterventionData?.data && (
            <Card variant="bordered">
              <h3 className="text-lg font-medium text-brown-900 mb-4">
                Données du projet
              </h3>

              {/* Project Type */}
              {fullInterventionData.data.project_type && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-brown-700 mb-2">Type de projet</h4>
                  <Badge variant="blue" size="md">
                    {fullInterventionData.data.project_type}
                  </Badge>
                </div>
              )}

              {/* BMAD Markdown (if exists) */}
              {fullInterventionData.data.bmad_markdown && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-brown-700 mb-2">Document BMAD</h4>
                  <div className="bg-beige-50 rounded-lg p-4 text-sm text-brown-700 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-sans">
                      {fullInterventionData.data.bmad_markdown}
                    </pre>
                  </div>
                </div>
              )}

              {/* Answers (if exists) */}
              {fullInterventionData.data.answers && Object.keys(fullInterventionData.data.answers).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-brown-700 mb-2">Réponses du questionnaire</h4>
                  <div className="bg-beige-50 rounded-lg p-4 space-y-3">
                    {Object.entries(fullInterventionData.data.answers).map(([key, value]) => (
                      <div key={key} className="border-b border-beige-200 pb-2 last:border-0">
                        <p className="text-xs text-brown-500 mb-1">{key}</p>
                        <p className="text-sm text-brown-700">
                          {Array.isArray(value) ? value.join(', ') : String(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw data (fallback if no specific fields) */}
              {!fullInterventionData.data.bmad_markdown &&
               !fullInterventionData.data.answers &&
               fullInterventionData.data && (
                <div>
                  <h4 className="text-sm font-medium text-brown-700 mb-2">Données brutes</h4>
                  <div className="bg-beige-50 rounded-lg p-4 text-xs text-brown-700 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-mono">
                      {JSON.stringify(fullInterventionData.data, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Conversation (if exists) */}
          {intervention.conversationId && conversation && (
            <Card variant="bordered">
              <h3 className="text-lg font-medium text-brown-900 mb-4">
                Conversation IA
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {conversation.messages.slice(-10).map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      'p-3 rounded-lg text-sm',
                      msg.role === 'user' ? 'bg-beige-100 ml-8' : 'bg-mint-50 mr-8'
                    )}
                  >
                    <span className="font-medium">
                      {msg.role === 'user' ? 'Client' : 'IA'}:
                    </span>{' '}
                    {msg.content}
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/ai/questionnaire/${intervention.conversationId}`)}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Voir la conversation complète
                </Button>
              </div>
            </Card>
          )}

          {/* Actions */}
          <Card variant="bordered">
            <h3 className="text-lg font-medium text-brown-900 mb-4">Actions</h3>
            <div className="flex flex-wrap gap-3">
              {(intervention.status === 'pending' || intervention.status === 'submitted' || intervention.status === 'draft') && (
                <>
                  <Button onClick={handleTakeOver} disabled={updateIntervention.isPending}>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Prendre en charge
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus('dismissed')}
                    disabled={updateIntervention.isPending}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Ignorer
                  </Button>
                </>
              )}

              {intervention.status === 'in_progress' && (
                <Button onClick={() => setShowResponseModal(true)}>
                  <Send className="w-4 h-4 mr-2" />
                  Résoudre l'intervention
                </Button>
              )}

              <Button variant="outline" onClick={() => setShowEmailModal(true)}>
                <Mail className="w-4 h-4 mr-2" />
                Envoyer un email
              </Button>

              {/* Create Project button for creation_projet requests */}
              {(intervention?.reason === 'creation_projet' || fullInterventionData?.data?.project_type) &&
               intervention?.status !== 'completed' && (
                <Button
                  onClick={() => setShowCreateProjectModal(true)}
                  disabled={isCreatingProject}
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Créer le projet
                </Button>
              )}

              <div className="ml-auto">
                <Select
                  value={intervention.status}
                  onChange={(e) => handleUpdateStatus(e.target.value)}
                  className="w-48"
                >
                  {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                    <option key={value} value={value}>
                      {config.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar - Client info */}
        <div className="space-y-6">
          <Card variant="bordered">
            <h3 className="text-lg font-medium text-brown-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Informations client
            </h3>

            {clientUser ? (
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-brown-500 mb-1">Email</p>
                  <a
                    href={`mailto:${clientUser.email}`}
                    className="text-sm text-mint-600 hover:underline flex items-center gap-1 break-all"
                  >
                    <Mail className="w-3 h-3 flex-shrink-0" />
                    {clientUser.email}
                  </a>
                </div>

                <div>
                  <p className="text-xs text-brown-500 mb-1">ID Utilisateur</p>
                  <p className="text-xs text-brown-600 font-mono break-all">{clientUser.id}</p>
                </div>

                <div>
                  <p className="text-xs text-brown-500 mb-1">Créé le</p>
                  <p className="text-xs text-brown-600">
                    {new Date(clientUser.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-brown-500">Aucune information utilisateur disponible</p>
            )}
          </Card>

          {/* Timeline */}
          <Card variant="bordered">
            <h3 className="text-lg font-medium text-brown-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Chronologie
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-mint-500 rounded-full mt-1.5"></div>
                <div>
                  <p className="font-medium text-brown-900">Créée</p>
                  <p className="text-brown-600 text-xs">
                    {new Date(intervention.createdAt).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>

              {intervention.resolvedAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-mint-500 rounded-full mt-1.5"></div>
                  <div>
                    <p className="font-medium text-brown-900">Résolue</p>
                    <p className="text-brown-600 text-xs">
                      {new Date(intervention.resolvedAt).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Email Modal */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="Envoyer un email au client"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Destinataire
            </label>
            <Input value={clientUser?.email || 'N/A'} disabled />
          </div>

          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">Objet</label>
            <Input
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Objet de l'email..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">Message</label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Votre message..."
              className="w-full h-40 px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-beige-200">
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={!emailSubject || !emailBody || !clientUser?.email}
            >
              <Mail className="w-4 h-4 mr-2" />
              Envoyer
            </Button>
          </div>
        </div>
      </Modal>

      {/* Response Modal */}
      <Modal
        isOpen={showResponseModal}
        onClose={() => setShowResponseModal(false)}
        title="Résoudre l'intervention"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Réponse au client
            </label>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Votre réponse..."
              className="w-full h-32 px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Notes internes (optionnel)
            </label>
            <Input
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Notes pour l'équipe..."
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-beige-200">
            <Button variant="outline" onClick={() => setShowResponseModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmitResponse}
              disabled={!responseText.trim() || resolveIntervention.isPending}
            >
              {resolveIntervention.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Résoudre
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Project Modal */}
      <Modal
        isOpen={showCreateProjectModal}
        onClose={() => setShowCreateProjectModal(false)}
        title="Créer un projet"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">
              📋 Création du projet
            </h4>
            <p className="text-sm text-blue-700">
              Cette action va créer un nouveau projet avec toutes les informations de cette demande :
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
              <li>Titre : {intervention?.lastUserMessage}</li>
              <li>Type : {fullInterventionData?.data?.project_type || 'Non spécifié'}</li>
              <li>Client : {clientUser?.full_name || 'Non spécifié'} ({clientUser?.email || 'N/A'})</li>
              {clientUser?.company && <li>Entreprise : {clientUser.company}</li>}
              {fullInterventionData?.data?.answers?.q4_objectives && (
                <li>Objectifs : {Array.isArray(fullInterventionData.data.answers.q4_objectives) ? fullInterventionData.data.answers.q4_objectives.join(', ') : fullInterventionData.data.answers.q4_objectives}</li>
              )}
              {fullInterventionData?.data?.answers?.q_features_mvp && (
                <li>Fonctionnalités : {fullInterventionData.data.answers.q_features_mvp}</li>
              )}
              {fullInterventionData?.data?.bmad_markdown && (
                <li>Document BMAD stocké dans les métadonnées (admin)</li>
              )}
              {fullInterventionData?.data?.answers && (
                <li>{Object.keys(fullInterventionData.data.answers).length} réponses du questionnaire</li>
              )}
            </ul>
          </div>

          <div className="bg-mint-50 border border-mint-200 rounded-lg p-4">
            <h4 className="font-medium text-mint-900 mb-2">
              ✅ Après la création
            </h4>
            <p className="text-sm text-mint-700">
              • Le projet sera créé avec toutes les infos client (entreprise, objectifs, etc.)<br />
              • Une description client-friendly sera générée automatiquement<br />
              • Le document BMAD sera conservé dans les métadonnées (visible admin uniquement)<br />
              • Une image vitrine sera attribuée aléatoirement<br />
              • L'intervention sera marquée comme "Terminée"<br />
              • Vous serez redirigé vers la page du projet
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-beige-200">
            <Button variant="outline" onClick={() => setShowCreateProjectModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={isCreatingProject}
            >
              {isCreatingProject ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Création...
                </>
              ) : (
                <>
                  <Briefcase className="w-4 h-4 mr-2" />
                  Créer le projet
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
